import { Injectable } from '@angular/core';
import { isFiniteNumber } from '@lib/number';
import { measurement } from '@lib/measurement';
import { getUserIANATimezone } from '@lib/timezone-preference-list';
import { Color } from '@lib/color';
import { User } from '@models';
import { Vehicle, VehicleLocationQuery } from '@models';
import { vehiclesDefaultColors } from 'src/colors';

@Injectable({
	providedIn: 'root',
})
export class LocationTelemetryService {
	constructor(private readonly user: User) { }

	public getVehicleTelemetry(
		vehicleId: number,
		vehiclesPathLocations: VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] = [],
	): VehicleLocationQuery.Interval.VehicleLocationsWithPathColor | undefined {
		return vehiclesPathLocations.find((telemetry) => telemetry.vehicleId === vehicleId);
	}

	public getFleetAverageSpeedMph(
		vehiclesPathLocations: VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] = [],
	): number {
		const telemetry = vehiclesPathLocations.filter((item) => item.vehicleLocations.length > 1);

		if (telemetry.length === 0) {
			return 0;
		}

		return telemetry.reduce((total, item) => total + item.averageSpeedMph, 0) / telemetry.length;
	}

	public getTotalIdleTimeSeconds(
		vehiclesPathLocations: VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] = [],
	): number {
		return vehiclesPathLocations.reduce((total, item) => total + item.idleTimeSeconds, 0);
	}

	public formatSpeed(speedMph: number | null | undefined): string {
		return `${Math.round(speedMph ?? 0)} mph`;
	}

	public formatOdometer(odometerMeters: number | null | undefined): string {
		if (!isFiniteNumber(odometerMeters)) {
			return '—';
		}

		const distanceUnit = this.user.distanceUnit as measurement.Unit;
		const unit = measurement.unitMeta[distanceUnit] ? distanceUnit : 'meter';
		const value = measurement.convert(odometerMeters, 'meter', unit);

		return `${value.toLocaleString(undefined, {
			maximumFractionDigits: measurement.unitMeta[unit].decimalCount,
		})} ${measurement.abbreviation(unit)}`;
	}

	public formatDuration(totalSeconds: number | null | undefined): string {
		const seconds = Math.max(0, Math.floor(totalSeconds ?? 0));
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);

		if (hours > 0) {
			return `${hours}h ${minutes}m`;
		}

		return `${minutes}m`;
	}

	public formatIdleEventTime(epochSeconds: number): string {
		return this.formatTime(epochSeconds, false);
	}

	public formatPlaybackTimestamp(epochSeconds: number): string {
		return this.formatTime(epochSeconds, true);
	}

	private formatTime(epochSeconds: number, includeSeconds: boolean): string {
		const options: Intl.DateTimeFormatOptions = {
			hour: 'numeric',
			minute: '2-digit',
			...(includeSeconds ? { second: '2-digit' } : {}),
			hour12: this.user.timeFormat !== 'h23',
		};

		if (this.user.timezone && this.user.timezone !== 'Default') {
			options.timeZone = getUserIANATimezone(this.user.timezone);
		}

		try {
			return new Intl.DateTimeFormat(undefined, options).format(new Date(epochSeconds * 1000));
		} catch {
			delete options.timeZone;
			return new Intl.DateTimeFormat(undefined, options).format(new Date(epochSeconds * 1000));
		}
	}

	public mapVehicleColors(
		vehicleLocationsResponse: VehicleLocationQuery.Interval.Response,
		vehicleProperties: [Vehicle, Color, boolean][],
	): VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] {
		return vehicleLocationsResponse.tracePaths.map((vehicleLocations) => {
			const vehicleColor = vehicleProperties.find((vehicle) => vehicle[0].id === vehicleLocations.vehicleId)?.[1]
				?? vehiclesDefaultColors[0];

			return {
				vehicleName: vehicleLocations.vehicleName,
				vehicleId: vehicleLocations.vehicleId,
				color: vehicleColor,
				vehicleLocations: vehicleLocations.vehicleLocations,
				locationTimes: vehicleLocations.locationTimes ?? [],
				locationSpeeds: vehicleLocations.locationSpeeds ?? [],
				locationOdometers: vehicleLocations.locationOdometers ?? [],
				averageSpeedMph: vehicleLocations.averageSpeedMph,
				latestOdometer: vehicleLocations.latestOdometer,
				idleTimeSeconds: vehicleLocations.idleTimeSeconds,
				idleIntervals: vehicleLocations.idleIntervals,
			};
		});
	}
}
